import NimgcenterSearchQuery from '../item-type/nimgcenter/SearchQuery';
import type { ItemFilter } from './ItemUtil';

type AdvancedSearchQueryData = Map<string, URLSearchParams>;

class AdvancedSearchQuery {
    private dataset: AdvancedSearchQueryData = new Map();

    empty() {
        let ret = true;
        this.dataset.forEach((data) => {
            data.forEach((value) => {
                value = value.trim();
                if (value.length !== 0) {
                    ret = false;
                }
            });
        });
        return ret;
    }

    get(type: string, key: string) {
        return this.dataset.get(type.trim())?.get(key.trim()) ?? '';
    }

    has(type: string, key: string) {
        return this.dataset.get(type.trim())?.has(key.trim()) ?? false;
    }

    hasType(type: string) {
        return this.dataset.has(type.trim());
    }

    set(type: string, key: string, value: string) {
        type = type.trim();
        key = key.trim();
        const data: URLSearchParams = this.dataset.has(type)
            ? (this.dataset.get(type) as URLSearchParams)
            : new URLSearchParams();
        data.set(key, value);
        this.dataset.set(type, data);
    }

    delete(type: string, key: string) {
        type = type.trim();
        key = key.trim();
        const data = this.dataset.get(type);
        if (typeof data === 'undefined') {
            return;
        }
        data.delete(key);
    }

    deleteType(type: string) {
        this.dataset.delete(type.trim());
    }

    clear() {
        this.dataset.clear();
    }

    getQueryParams() {
        const ret: URLSearchParams = new URLSearchParams();
        this.dataset.forEach((data, type) => {
            data.forEach((value, key) => {
                value = value.trim();
                if (value.length > 0) {
                    ret.set(`${type}.${key}`, value);
                }
            });
        });
        return ret;
    }

    setByQueryString(queryString: string) {
        const query = new URLSearchParams(queryString);
        query.forEach((v, k) => {
            const [type, ...key] = k.split('.');
            // Only `<item type>.<field>` keys are search fields (not `ml_lang` or the list settings).
            if (key.length === 0) {
                return;
            }
            this.set(type, key.join('.'), v);
        });
    }

    getSearchFilter(): ItemFilter {
        const regex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const filter: ItemFilter[] = [];
        this.dataset.forEach((data, type) => {
            // Brain coordinates are searched by distance, not by matching text.
            if (type === 'nimgcenter') {
                filter.push(NimgcenterSearchQuery.getAdvancedSearchFilter(data));
                return;
            }
            const filter2: ItemFilter[] = [];
            filter2.push({
                item_type_name: `xnp${type}`,
            });
            data.forEach((value, key) => {
                const keys = key.split('.');
                if (keys[0] === 'file' && keys.length === 3) {
                    filter2.push({
                        file: {
                            $elemMatch: {
                                file_type_name: keys[1],
                                [keys[2]]: { $regex: [regex(value), 'i'] },
                            },
                        },
                    });
                } else {
                    filter2.push({ [key]: { $regex: [regex(value), 'i'] } });
                }
            });
            filter.push({ $and: filter2 });
        });
        return { $or: filter };
    }
}

export default AdvancedSearchQuery;
